// src/components/admin/WorkHoursChart.tsx
// Gráfico de produtividade administrativa - IDENTIDADE GRAIA

import React, { useEffect, useState } from 'react';
import { Clock, Calendar, Zap, Activity } from 'lucide-react';
import './WorkHoursChart.css';

interface WeekData {
    week: string;
    hours: number;
    sessions: number;
}

interface WorkDistributionItem {
    label: string;
    value: number;
    color: string;
}

interface WorkStats {
    totalHours: number;
    weeklyData: WeekData[];
    todayHours: number;
    thisWeekHours: number;
    streak: number;
    activeDaysThisWeek: number;
    workDistribution: WorkDistributionItem[];
}

// Pizza chart SVG puro
function PieChart({ data }: { data: WorkDistributionItem[] }) {
    const total = data.reduce((s, d) => s + d.value, 0) || 1;
    let cumAngle = 0;

    const slices = data.map(d => {
        const angle = (d.value / total) * 360;
        const start = cumAngle;
        cumAngle += angle;
        return { ...d, startAngle: start, angle };
    });

    function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
        const rad = ((angleDeg - 90) * Math.PI) / 180;
        return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    }

    function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
        const start = polarToXY(cx, cy, r, startAngle);
        const end = polarToXY(cx, cy, r, endAngle);
        const largeArc = endAngle - startAngle > 180 ? 1 : 0;
        return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
    }

    const allZero = data.every(d => d.value === 0);

    return (
        <div className="wrk-hrs-pie-wrapper">
            <svg viewBox="0 0 120 120" className="wrk-hrs-pie-svg">
                {allZero ? (
                    <circle cx="60" cy="60" r="50" fill="#E5E7EB" />
                ) : (
                    slices.map((s, i) =>
                        s.angle > 0 ? (
                            <path
                                key={i}
                                d={describeArc(60, 60, 50, s.startAngle, s.startAngle + s.angle)}
                                fill={s.color}
                                stroke="#fff"
                                strokeWidth="1.5"
                            />
                        ) : null
                    )
                )}
            </svg>
            <div className="wrk-hrs-pie-legend">
                {data.map((d, i) => (
                    <div key={i} className="wrk-hrs-pie-item">
                        <span className="wrk-hrs-pie-dot" style={{ backgroundColor: d.color }} />
                        <span className="wrk-hrs-pie-label">{d.label}</span>
                        <span className="wrk-hrs-pie-value">{d.value}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

const WorkHoursChart: React.FC = () => {
    const [stats, setStats] = useState<WorkStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/activity/work-stats', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setStats(data);
                }
            } catch (err) {
                console.error('Erro ao carregar stats de trabalho:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) {
        return (
            <div className="wrk-hrs-chart">
                <h3>Produtividade Administrativa</h3>
                <div className="wrk-hrs-loading">
                    <div className="wrk-hrs-spinner"></div>
                    <p>Carregando dados</p>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="wrk-hrs-chart">
                <h3>Produtividade Administrativa</h3>
                <div className="wrk-hrs-empty">
                    <Clock size={48} />
                    <p>Nenhum dado de trabalho registrado ainda</p>
                    <p className="wrk-hrs-note">O sistema rastreará suas horas automaticamente</p>
                </div>
            </div>
        );
    }

    const maxHours = Math.max(...stats.weeklyData.map(w => w.hours), 1);

    const formatWeekLabel = (weekStr: string) => {
        const date = new Date(weekStr);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${day}/${month}`;
    };

    const BLUE_PALETTE = ['#003BD1', '#2563EB', '#93C5FD', '#60A5FA', '#1D4ED8'];
    const rawDist: WorkDistributionItem[] = stats.workDistribution || [
        { label: 'Criação de conteúdo', value: 0, color: '#003BD1' },
        { label: 'Revisão/Edição', value: 0, color: '#2563EB' },
        { label: 'Gestão', value: 0, color: '#93C5FD' }
    ];
    const pieData: WorkDistributionItem[] = rawDist.map((item, i) => ({
        ...item,
        color: BLUE_PALETTE[i % BLUE_PALETTE.length],
    }));

    return (
        <div className="wrk-hrs-chart">
            <div className="wrk-hrs-header">
                <h3>Produtividade Administrativa</h3>
                <div className="wrk-hrs-period">
                    <Calendar size={16} />
                    Últimas 4 semanas
                </div>
            </div>

            {/* Cards: Hoje / Esta Semana / Streak / Dias Ativos */}
            <div className="wrk-hrs-summary">
                <div className="wrk-hrs-stat-card">
                    <div className="wrk-hrs-stat-icon">
                        <Clock size={20} />
                    </div>
                    <div className="wrk-hrs-stat-content">
                        <div className="wrk-hrs-stat-value">{stats.todayHours?.toFixed(1) ?? '0.0'}h</div>
                        <div className="wrk-hrs-stat-label">Hoje</div>
                    </div>
                </div>

                <div className="wrk-hrs-stat-card">
                    <div className="wrk-hrs-stat-icon">
                        <Calendar size={20} />
                    </div>
                    <div className="wrk-hrs-stat-content">
                        <div className="wrk-hrs-stat-value">{stats.thisWeekHours?.toFixed(1) ?? '0.0'}h</div>
                        <div className="wrk-hrs-stat-label">Esta Semana</div>
                    </div>
                </div>

                <div className="wrk-hrs-stat-card">
                    <div className="wrk-hrs-stat-icon">
                        <Zap size={20} />
                    </div>
                    <div className="wrk-hrs-stat-content">
                        <div className="wrk-hrs-stat-value">{stats.streak ?? 0}</div>
                        <div className="wrk-hrs-stat-label">Streak</div>
                    </div>
                </div>

                <div className="wrk-hrs-stat-card">
                    <div className="wrk-hrs-stat-icon">
                        <Activity size={20} />
                    </div>
                    <div className="wrk-hrs-stat-content">
                        <div className="wrk-hrs-stat-value">{stats.activeDaysThisWeek ?? 0}/7</div>
                        <div className="wrk-hrs-stat-label">Dias Ativos</div>
                    </div>
                </div>
            </div>

            {/* Gráfico de barras semanal */}
            <div className="wrk-hrs-weekly-chart">
                <div className="wrk-hrs-chart-grid">
                    {stats.weeklyData.map((week, index) => {
                        const heightPercent = (week.hours / maxHours) * 100;
                        return (
                            <div key={index} className="wrk-hrs-bar-wrapper">
                                <div className="wrk-hrs-bar-container">
                                    <div
                                        className="wrk-hrs-bar"
                                        style={{
                                            '--bar-height': `${heightPercent}%`,
                                            animationDelay: `${index * 0.15}s`
                                        } as React.CSSProperties}
                                        data-tooltip={`${week.hours.toFixed(1)}h em ${week.sessions} sessões`}
                                    >
                                        {week.hours > 0 && (
                                            <span className="wrk-hrs-bar-value">
                                                {week.hours.toFixed(1)}h
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="wrk-hrs-bar-label">
                                    {formatWeekLabel(week.week)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Pizza: distribuição por categoria */}
            <div className="wrk-hrs-distribution">
                <div className="wrk-hrs-distribution-title">Distribuição por Tipo</div>
                <PieChart data={pieData} />
            </div>

            <p className="wrk-hrs-footer">
                Tempo em páginas de estudo (StudyPage/ResumoRead) não é contabilizado
            </p>
        </div>
    );
};

export default WorkHoursChart;
